import { Icon as IconifyIcon } from '@iconify/react';
import { CompositeFilterDescriptor, filterBy, FilterDescriptor } from '@progress/kendo-data-query';
import { Icon } from '@progress/kendo-react-common';
import {
  DropDownList,
  DropDownListChangeEvent,
  DropDownListFilterChangeEvent,
} from '@progress/kendo-react-dropdowns';
import { TextBox, TextBoxChangeEvent } from '@progress/kendo-react-inputs';
import { Error } from '@progress/kendo-react-labels';
import clsx from 'clsx';
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import 'src/style/view-shopping-lists/ShoppingListForm.scss';
import {
  ShoppingListFormField,
  ShoppingListFormValues,
} from 'src/types/view_shopping_lists/ShoppingListForm';
import { useContextSelector } from 'use-context-selector';
import { ViewShoppingListsContext } from './ViewShoppingListsContext';

interface ShoppingListFormProps {
  values: ShoppingListFormValues;
  errors: Partial<Record<ShoppingListFormField, string>>;
  touched: Partial<Record<ShoppingListFormField, boolean>>;
  onFieldChange: (field: ShoppingListFormField, value: string) => void;
  onFieldBlur: (field: ShoppingListFormField) => void;
}

const formatShoppingListGlCodeLabel = (code: string, description: string) =>
  `${code} ${description}`.trim();

const ShoppingListForm: FC<ShoppingListFormProps> = ({
  values,
  errors,
  touched,
  onFieldChange,
  onFieldBlur,
}) => {
  const { t } = useTranslation();
  const glCodeFieldRef = useRef<HTMLDivElement>(null);
  const glCodeOptions = useContextSelector(
    ViewShoppingListsContext,
    (context) => context.glCodeOptions
  );
  const areGLCodesLoading = useContextSelector(
    ViewShoppingListsContext,
    (context) => context.areGLCodesLoading
  );
  const glCodeDefaultItem = useMemo(
    () => ({
      label: t('Select One'),
      value: '',
    }),
    [t]
  );

  const glCodeDropdownOptions = useMemo(
    () =>
      (glCodeOptions ?? []).map((option) => ({
        label: formatShoppingListGlCodeLabel(option.code, option.description),
        value: option.value,
      })),
    [glCodeOptions]
  );

  const [filteredGlCodeDropdownOptions, setFilteredGlCodeDropdownOptions] =
    useState(glCodeDropdownOptions);

  useEffect(() => {
    setFilteredGlCodeDropdownOptions(glCodeDropdownOptions);
  }, [glCodeDropdownOptions]);

  const filterGlCodeOptions = useCallback(
    (filter: FilterDescriptor | CompositeFilterDescriptor) =>
      filterBy(glCodeDropdownOptions, filter),
    [glCodeDropdownOptions]
  );

  const handleGlCodeFilterChange = useCallback(
    (event: DropDownListFilterChangeEvent) => {
      setFilteredGlCodeDropdownOptions(filterGlCodeOptions(event.filter));
    },
    [filterGlCodeOptions]
  );

  const selectedGlCodeOption = useMemo(() => {
    if (!values.glCode) {
      return glCodeDefaultItem;
    }

    return (
      glCodeDropdownOptions.find((option) => option.value === values.glCode) ?? glCodeDefaultItem
    );
  }, [glCodeDefaultItem, glCodeDropdownOptions, values.glCode]);

  const handleListNameChange = useCallback(
    (event: TextBoxChangeEvent) => {
      onFieldChange('listName', String(event.value ?? ''));
    },
    [onFieldChange]
  );

  const handleGlCodeChange = useCallback(
    (event: DropDownListChangeEvent) => {
      onFieldChange('glCode', String(event.value?.value ?? ''));
    },
    [onFieldChange]
  );

  const handleClearGlCode = useCallback(() => {
    onFieldChange('glCode', '');
  }, [onFieldChange]);

  const syncGlCodePopupWidth = useCallback(() => {
    requestAnimationFrame(() => {
      const anchorWidth = glCodeFieldRef.current?.offsetWidth;
      const popup = document.querySelector(
        '.shopping-list-form__gl-code-popup.k-popup'
      ) as HTMLElement | null;

      if (anchorWidth && popup) {
        popup.style.width = `${anchorWidth}px`;
      }
    });
  }, []);

  const showListNameError = Boolean(errors.listName && touched.listName);

  const customNoData = () => (
    <>
      <div className="p-4 text-center text-gray-500 text-sm">
        <Icon name="warning" className="text-2xl mb-2" />
        <p>NO DATA FOUND.</p>
      </div>
    </>
  );

  return (
    <div className="shopping-list-form px-[32px] pb-6">
      <div className="flex max-w-[420px] flex-col gap-5">
        <div className="flex flex-col gap-1">
          <label className="text-sm text-gray-500" htmlFor="shopping-list-name">
            {t('List Name')}
            <span className="text-error ml-0.5">*</span>
          </label>
          <TextBox
            id="shopping-list-name"
            name="shopping-list-name"
            value={values.listName}
            onChange={handleListNameChange}
            onBlur={() => onFieldBlur('listName')}
            size="medium"
            valid={!showListNameError}
            maxLength={742}
            className={clsx(
              'shopping-list-form__text-input',
              showListNameError && '[&_.k-input-inner]:!border-2 [&_.k-input-inner]:!border-error'
            )}
          />
          {showListNameError && errors.listName && (
            <Error id="shopping-list-name-error">{errors.listName}</Error>
          )}
        </div>

        <div className="flex flex-col gap-1">
          <span className="text-sm text-gray-500">{t('Owner')}</span>
          <TextBox
            id="shopping-list-owner"
            name="shopping-list-owner"
            value={values.owner}
            disabled
            size="medium"
            className="shopping-list-form__owner-input"
          />
        </div>

        <div ref={glCodeFieldRef} className="flex flex-col gap-1">
          <label className="text-sm text-gray-500" htmlFor="shopping-list-gl-code">
            {t('GL Code')}
          </label>
          <div className="relative">
            <DropDownList
              id="shopping-list-gl-code"
              data={filteredGlCodeDropdownOptions}
              textField="label"
              dataItemKey="value"
              value={selectedGlCodeOption}
              onChange={handleGlCodeChange}
              onOpen={syncGlCodePopupWidth}
              size="medium"
              filterable
              onFilterChange={handleGlCodeFilterChange}
              disabled={areGLCodesLoading}
              listNoDataRender={customNoData}
              className={clsx(
                'shopping-list-form__gl-code-dropdown',
                values.glCode && 'shopping-list-form__gl-code-dropdown--clearable'
              )}
              popupSettings={{
                className: 'shopping-list-form__gl-code-popup',
              }}
            />
            {values.glCode && (
              <button
                type="button"
                aria-label={t('clearGlCode')}
                className="shopping-list-form__gl-code-clear"
                onMouseDown={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                }}
                onClick={handleClearGlCode}
              >
                <IconifyIcon height="16" width="16" color="#AAAAAA" icon="lucide:x" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShoppingListForm;
